import { Capacitor } from "@capacitor/core";
import SworkitMobile, {
  HealthLogSearch,
  NativeWorkoutLog,
} from "../global/sworkit-mobile/sw-mobile";
import { getHealthLogType } from "../global/utils";
import { User, WorkoutLog } from "@sworkit/api";
import healthIntegration from "../stencil-store/health.store";
import { syncFromHealth, syncToHealth } from "../global/health";
import { alertController, AlertOptions } from "@ionic/core";
import { translate } from "../global/translate";
import workoutLogs from "../stencil-store/workout-logs.store";
import { recentWorkoutLogsListener } from "../global/firestore-workout-logs";
import { Unsubscribe } from "firebase/firestore";

let syncingToHealthApp;

export const syncWorkoutLogsToHealth = async (workoutLogs: WorkoutLog[]) => {
  const { writeEnabledIntegrations, availableIntegrations } = healthIntegration;
  if (
    !writeEnabledIntegrations?.length ||
    syncingToHealthApp ||
    !workoutLogs?.length
  )
    return;
  for (const integration of writeEnabledIntegrations) {
    if (!availableIntegrations?.includes(integration)) {
      // health enabled (toggled on) but not authorized
      return showNoPermissionAlert();
    }

    syncingToHealthApp = true;

    const logsToSync =
      workoutLogs?.filter(
        (log) =>
          !log.savedToHealthIntegrations?.includes(integration) &&
          log.shouldSaveToHealth,
      ) || [];
    for (const log of logsToSync) {
      try {
        console.log("sync log to health", log);
        await syncToHealth(log);
      } catch (err) {
        console.error(err);
      }
    }
    syncingToHealthApp = false;
  }
};

export const syncWorkoutLogsFromHealth = async (user: User) => {
  const { readEnabledIntegrations, availableIntegrations } = healthIntegration;
  if (!readEnabledIntegrations?.length || syncingToHealthApp) {
    return;
  }
  for (const integration of readEnabledIntegrations) {
    if (!availableIntegrations?.includes(integration)) {
      // health enabled (toggled on) but not available
      return showNoPermissionAlert();
    }

    const latestHealthLog = getMostRecentHealthLog(workoutLogs.recent);
    const twoMonthsAgo = new Date();
    twoMonthsAgo.setMonth(twoMonthsAgo.getMonth() - 2);
    const startTime = latestHealthLog
      ? new Date(latestHealthLog.timestampMillis)
      : twoMonthsAgo;
    const { workoutLogs: duplicateWorkoutLogs } = await readFromHealth({
      startTime,
      integration,
    });
    console.log("got workout logs from health", duplicateWorkoutLogs);
    //todo: fix this natively! remove duplicates here ---
    const workoutLogsNative: NativeWorkoutLog[] = [];
    for (const log of duplicateWorkoutLogs) {
      if (
        !workoutLogsNative.find((wl) => wl.externalUuid === log.externalUuid)
      ) {
        workoutLogsNative.push(log);
      }
    }
    //todo ends ---
    const workoutLogsToSave = getHealthWorkoutsThatAreNotSaved(
      workoutLogs.recent,
      workoutLogsNative,
    );
    if (workoutLogsToSave?.length) {
      for (const workout of workoutLogsToSave) {
        try {
          await syncFromHealth(workout, user);
        } catch (err) {
          console.error(err);
        }
      }
      // get logs from health (make sure they're "hydrated" with user data)
      // showSyncedFromHealthToast(workoutLogsToSave.length); todohealth
    }
  }
};

async function showNoPermissionAlert() {
  const alertOptions: AlertOptions = {
    header: translate("ENABLE_HEALTH_IN_SETTINGS"),
    buttons: [
      {
        text: translate("OK"),
      },
    ],
  };
  const alert = await alertController.create(alertOptions);
  return alert.present();
}

const getHealthWorkoutsThatAreNotSaved = (
  loggedWorkouts: WorkoutLog[],
  freshHealthWorkouts: NativeWorkoutLog[],
) => {
  const uuids = loggedWorkouts.map((workout) => workout.externalUuid);
  const newWorkoutsToSave = freshHealthWorkouts.filter((workout) => {
    return (
      workout?.externalUuid && uuids && !uuids.includes(workout?.externalUuid)
    );
  });
  return newWorkoutsToSave;
};

const getMostRecentHealthLog = (recentLogs: WorkoutLog[]) => {
  const platformLogType = getHealthLogType();
  return recentLogs.find((log) => log.logType === platformLogType);
};

const readFromHealth = (options: HealthLogSearch) => {
  const platform = Capacitor.getPlatform();
  const search =
    platform === "ios"
      ? options
      : {
          startTime: options?.startTime?.valueOf()?.toString(),
          endTime: options?.endTime?.valueOf()?.toString(),
          limit: options.limit,
          integration: options.integration,
        };
  console.log("syncing from health", search);
  return SworkitMobile.healthRead(search);
};

let recentWorkoutLogsListenerSubscription: Unsubscribe;
export const listenToRecentWorkouts = (subscribe: boolean) => {
  if (recentWorkoutLogsListenerSubscription) return;
  if (!subscribe) {
    recentWorkoutLogsListenerSubscription?.();
    return;
  }
  recentWorkoutLogsListenerSubscription = recentWorkoutLogsListener(
    25,
    (recentWorkoutLogs) => {
      workoutLogs.recent = [...recentWorkoutLogs];
      //sync to health
      syncWorkoutLogsToHealth(recentWorkoutLogs);
    },
  );
  return recentWorkoutLogsListenerSubscription;
};
